import { cn } from "@/lib/utils";
import { AlertCircle, Loader } from "lucide-react";
import type { FpToolCallMessagePart } from "../../../../../../schemas/codegen";
import type { MessageCardIcon } from "./index";

export type BaseMessageCardProps = {
  icon: MessageCardIcon;
  title: React.ReactNode;
  additionalContent?: React.ReactNode;
  className?: string;
} & Pick<FpToolCallMessagePart, "status">;

/**
 * Renders the status icon based on the current status
 */
export function getStatusIcon(status: FpToolCallMessagePart["status"]) {
  if (status === "pending") {
    return <Loader className="w-3 h-3 animate-spin" />;
  }
  if (status === "error") {
    return <AlertCircle className="w-3 h-3 text-warning" />;
  }
  return null;
}

/**
 * Base message card content that handles common layout and status display
 * This component provides the shared structure used by both SimpleMessageCard and ExpandableMessageCard
 */
export function BaseMessageCardContent({
  status,
  icon: Icon,
  title,
  additionalContent,
  className,
}: BaseMessageCardProps) {
  const statusIcon = getStatusIcon(status);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Icon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">{title}</span>
          {additionalContent}
        </div>
      </div>
      <div className="flex-shrink-0">{statusIcon}</div>
    </div>
  );
}
